import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, Zap, ShieldAlert, Clock, TrendingUp, Radio, ChevronRight, ChevronDown, Shield, XCircle, CheckCircle, Loader, AlertTriangle, Eye, FileText, Hash } from 'lucide-react';
import { refreshAccessToken } from '../services/auth';
import { createReconnectingEventSource } from '../lib/reconnectingEventSource';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS — Matches AgentTrustCenterPage Credo.ai aesthetic
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
};

const S = {
  0: '#ffffff', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
};

/* ═══════════════════════════════════════════════════════════════════════════════
   CSS Keyframes (injected once)
   ═══════════════════════════════════════════════════════════════════════════════ */

const LiveStyles = () => (
  <style>{`
    @keyframes lecPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
    @keyframes lecSlideIn { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes lecSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .lec-card:hover { border-color: ${BRAND.primary} !important; box-shadow: 0 2px 8px rgba(226,74,58,0.08) !important; }
    .lec-card-new { animation: lecSlideIn 0.4s ease-out; }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   STAT CHIP
   ═══════════════════════════════════════════════════════════════════════════════ */

const StatChip = ({ icon: Icon, value, label, color, accentBg }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
    background: accentBg || S[50], borderRadius: 10, border: `1px solid ${S[200]}`,
    minWidth: 130,
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8, background: accentBg || S[100],
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <Icon size={15} color={color || S[600]} />
    </div>
    <div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || S[900], lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: S[400], lineHeight: 1.2, marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════════
   MINI PIPELINE (6-stage for GEL, single checkmark for MCP)
   ═══════════════════════════════════════════════════════════════════════════════ */

const MiniPipeline = ({ event }) => {
  if (event.type === 'mcp') {
    const stages = event.policyStages || [];
    const hasRule = event.ruleId && event.ruleId !== 'none';

    // If we have inline stage data, show a mini pipeline like GEL
    if (stages.length > 0) {
      const enforcementStages = stages.filter(s => s.stage !== 'Policy Bindings Resolved');
      return (
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {enforcementStages.map((stage, i) => {
            const isBlocked = stage.decision?.toLowerCase() === 'blocked';
            const isWarned = stage.violation && stage.decision?.toLowerCase() !== 'blocked';
            const bg = isBlocked ? '#f43f5e' : isWarned ? '#f59e0b' : '#10b981';
            const stageInfo = STAGE_LABELS[stage.stage];
            return (
              <React.Fragment key={i}>
                <div title={stageInfo?.label || stage.stage} style={{
                  width: 16, height: 16, borderRadius: 3, background: bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 8, color: '#fff', fontWeight: 700,
                }}>{i}</div>
                {i < enforcementStages.length - 1 && <span style={{ color: S[300], fontSize: 8 }}>›</span>}
              </React.Fragment>
            );
          })}
          {hasRule && (
            <div style={{
              fontSize: 9, color: S[500], fontFamily: "'Consolas', monospace",
              padding: '1px 4px', borderRadius: 3, background: S[100], marginLeft: 2,
            }}>R{event.ruleVersion || '?'}</div>
          )}
        </div>
      );
    }

    // Fallback: single checkmark for events without stage data
    const bg = event.decision === 'ALLOWED' ? '#10b981'
             : event.decision === 'BLOCKED' ? '#f43f5e'
             : event.decision === 'FAILED' ? '#f59e0b' : '#10b981';
    return (
      <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <div style={{
          width: 16, height: 16, borderRadius: 4, background: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, color: '#fff', fontWeight: 700,
        }}>✓</div>
        {hasRule && (
          <div style={{
            fontSize: 9, color: S[500], fontFamily: "'Consolas', monospace",
            padding: '1px 4px', borderRadius: 3, background: S[100],
          }}>R{event.ruleVersion || '?'}</div>
        )}
      </div>
    );
  }

  // GEL 6-phase pipeline: Initiation → Pre-Check → Executing → Post-Check → Sealed → Completed
  const phaseMap = { 'PENDING': 0, 'PRE_CHECK': 1, 'EXECUTING': 2, 'POST_CHECK': 3, 'SEALED': 4, 'COMPLETED': 5 };
  const phaseLabels = ['Init', 'Pre', 'Exec', 'Post', 'Seal', 'Done'];
  const currentIdx = phaseMap[event.phase] ?? 0;
  const isBlocked = event.decision === 'BLOCKED' || event.phase === 'BLOCKED';
  const isFailed = event.decision === 'FAILED' || event.phase === 'FAILED';

  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {phaseLabels.map((label, i) => {
        let bg, color;
        if (isBlocked && i === currentIdx) {
          bg = '#f43f5e'; color = '#fff';
        } else if (isFailed && i === currentIdx) {
          bg = '#f59e0b'; color = '#fff';
        } else if (i < currentIdx || (i === currentIdx && (event.phase === 'COMPLETED'))) {
          bg = '#10b981'; color = '#fff';
        } else if (i === currentIdx) {
          bg = '#f59e0b'; color = '#fff';
        } else {
          bg = S[200]; color = S[400];
        }

        return (
          <React.Fragment key={i}>
            <div title={label} style={{
              width: 16, height: 16, borderRadius: 3, background: bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 8, color, fontWeight: 700,
              ...(i === currentIdx && event.phase !== 'COMPLETED' && !isBlocked && !isFailed
                ? { animation: 'lecPulse 1.5s infinite' } : {}),
            }}>{i}</div>
            {i < 5 && <span style={{ color: S[300], fontSize: 8 }}>›</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   DECISION BADGE
   ═══════════════════════════════════════════════════════════════════════════════ */

const DecisionBadge = ({ decision, hasWarnings }) => {
  // Show WARNED badge if decision is ALLOWED but has warnings
  const displayDecision = (decision === 'ALLOWED' && hasWarnings) ? 'WARNED' : decision;

  const styles = {
    ALLOWED:   { bg: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: ACCENT.emerald.border },
    WARNED:    { bg: ACCENT.amber.bg, color: ACCENT.amber.text, border: ACCENT.amber.border },
    BLOCKED:   { bg: ACCENT.rose.bg, color: ACCENT.rose.text, border: ACCENT.rose.border },
    FAILED:    { bg: ACCENT.amber.bg, color: ACCENT.amber.text, border: ACCENT.amber.border },
    EXECUTING: { bg: ACCENT.amber.bg, color: ACCENT.amber.text, border: ACCENT.amber.border },
  };
  const s = styles[displayDecision] || styles.ALLOWED;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      letterSpacing: '0.02em',
    }}>{displayDecision}</span>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   PHASE ICON
   ═══════════════════════════════════════════════════════════════════════════════ */

const PhaseIcon = ({ event }) => {
  const isMcp = event.type === 'mcp';
  const isExecuting = event.phase === 'EXECUTING' || event.decision === 'EXECUTING';
  const isBlocked = event.decision === 'BLOCKED' || event.phase === 'BLOCKED';
  const isFailed = event.decision === 'FAILED' || event.phase === 'FAILED';

  let bg, icon;
  if (isMcp) { bg = S[800]; icon = 'M'; }
  else if (isExecuting) { bg = '#f59e0b'; icon = '⟳'; }
  else if (isBlocked) { bg = '#f43f5e'; icon = '✕'; }
  else if (isFailed) { bg = '#f59e0b'; icon = '!'; }
  else { bg = '#10b981'; icon = '✓'; }

  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
      ...(isExecuting ? { animation: 'lecPulse 1.5s infinite' } : {}),
    }}>{icon}</div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   EXECUTION CARD
   ═══════════════════════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════════════════════════
   EVENT DETAIL PANEL — Expanded view inside the card
   ═══════════════════════════════════════════════════════════════════════════════ */

const DetailRow = ({ label, value, mono, icon: Icon }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0' }}>
    {Icon && <Icon size={13} color={S[400]} style={{ marginTop: 2, flexShrink: 0 }} />}
    <span style={{ fontSize: 11, fontWeight: 600, color: S[500], minWidth: 110, flexShrink: 0 }}>{label}</span>
    <span style={{
      fontSize: 12, color: S[800],
      fontFamily: mono ? "'Consolas', 'Monaco', monospace" : 'inherit',
      wordBreak: 'break-all',
    }}>
      {value || '—'}
    </span>
  </div>
);

/* ── Stage labels for the 6-layer enforcement pipeline ── */
const STAGE_LABELS = {
  'DETERMINISTIC_RULES': { label: 'Deterministic Rules', icon: '⚡', desc: 'L1 policy rules (regex, range, enum, rate-limit)' },
  'deterministic_rules': { label: 'Deterministic Rules', icon: '⚡', desc: 'L1 policy rules (regex, range, enum, rate-limit)' },
  'AUTONOMY': { label: 'Autonomy Validation', icon: '🔒', desc: 'Skill autonomy level is permitted' },
  'autonomy': { label: 'Autonomy Validation', icon: '🔒', desc: 'Skill autonomy level is permitted' },
  'SYSTEM_PROTECTION': { label: 'System Agent Protection', icon: '🛡️', desc: 'System resources are protected' },
  'system_protection': { label: 'System Agent Protection', icon: '🛡️', desc: 'System resources are protected' },
  'RBAC': { label: 'RBAC Constraint Check', icon: '👤', desc: 'User has sufficient role for the operation' },
  'rbac': { label: 'RBAC Constraint Check', icon: '👤', desc: 'User has sufficient role for the operation' },
  'GUARDRAIL': { label: 'Guardrail Evaluation', icon: '🚧', desc: 'Custom guardrail rules are satisfied' },
  'guardrail': { label: 'Guardrail Evaluation', icon: '🚧', desc: 'Custom guardrail rules are satisfied' },
  'POLICY_RAG': { label: 'Policy RAG Retrieval', icon: '📄', desc: 'Semantic policy context for LLM prompt injection' },
  'policy_rag': { label: 'Policy RAG Retrieval', icon: '📄', desc: 'Semantic policy context for LLM prompt injection' },
  'Policy Bindings Resolved': { label: 'Policy Bindings Resolved', icon: '🔗', desc: 'Compliance framework and rule bindings matched' },
};

const StageRow = ({ stage, index, totalStages }) => {
  const isBlocked = stage.decision?.toLowerCase() === 'blocked';
  const isWarned = stage.violation && stage.decision?.toLowerCase() !== 'blocked';
  const isBindings = stage.stage === 'Policy Bindings Resolved';
  const stageInfo = STAGE_LABELS[stage.stage] || {};
  const label = stageInfo.label || stage.stage || stage.ruleName || `Stage ${index + 1}`;

  // If this is the bindings summary row, render differently
  if (isBindings && stage.bindings) {
    return (
      <div style={{
        padding: '8px 10px', background: ACCENT.sky.bg, borderRadius: 4, fontSize: 12,
        borderLeft: `3px solid ${ACCENT.sky.icon}`, marginTop: 4,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 13 }}>🔗</span>
          <span style={{ fontWeight: 700, color: ACCENT.sky.text }}>
            Policy Bindings Resolved ({stage.bindingCount} bindings matched)
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {stage.bindings.map((b, i) => (
            <span key={i} style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: b.severity === 'BLOCK' ? ACCENT.rose.bg
                : b.severity === 'WARN' ? ACCENT.amber.bg : ACCENT.emerald.bg,
              color: b.severity === 'BLOCK' ? ACCENT.rose.text
                : b.severity === 'WARN' ? ACCENT.amber.text : ACCENT.emerald.text,
              border: `1px solid ${b.severity === 'BLOCK' ? ACCENT.rose.border
                : b.severity === 'WARN' ? ACCENT.amber.border : ACCENT.emerald.border}`,
            }}>
              {b.sourceTag === 'COMPLIANCE_FRAMEWORK' && <span title="Compliance Framework Rule">🏛️</span>}
              {b.ruleName || b.ruleType} · {b.scope || 'TENANT'} · {b.severity || 'INFO'}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '28px 1fr 90px 1fr',
      alignItems: 'center', gap: 10, padding: '7px 10px',
      background: isBlocked ? ACCENT.rose.bg : isWarned ? ACCENT.amber.bg : index % 2 === 0 ? S[50] : '#fff',
      borderRadius: 4, fontSize: 12,
      borderLeft: `3px solid ${isBlocked ? '#f43f5e' : isWarned ? '#f59e0b' : '#10b981'}`,
    }}>
      <span style={{
        width: 22, height: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isBlocked ? '#f43f5e' : isWarned ? '#f59e0b' : '#10b981',
        color: '#fff', fontSize: 10, fontWeight: 700,
      }}>{index}</span>
      <div>
        <div style={{ fontWeight: 600, color: S[800], display: 'flex', alignItems: 'center', gap: 6 }}>
          {stageInfo.icon && <span style={{ fontSize: 12 }}>{stageInfo.icon}</span>}
          {label}
          {stage.scope && (
            <span style={{
              fontSize: 9, padding: '1px 5px', borderRadius: 3,
              background: S[100], color: S[500], fontWeight: 600,
            }}>{stage.scope}</span>
          )}
        </div>
        {stageInfo.desc && <div style={{ fontSize: 10, color: S[400], marginTop: 1 }}>{stageInfo.desc}</div>}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, textAlign: 'center',
        background: isBlocked ? '#f43f5e' : isWarned ? '#f59e0b' : '#10b981',
        color: '#fff', textTransform: 'uppercase',
      }}>
        {isBlocked ? 'BLOCKED' : isWarned ? 'WARNED' : 'PASSED'}
      </span>
      <span style={{
        fontSize: 11, color: isBlocked ? ACCENT.rose.text : isWarned ? ACCENT.amber.text : S[500],
        fontStyle: stage.violation ? 'normal' : 'italic',
      }}>
        {stage.violation || stage.ruleName || 'No issues'}
      </span>
    </div>
  );
};

const EventDetailPanel = ({ event }) => {
  const isMcp = event.type === 'mcp';
  const stages = event.policyStages || [];
  const timestamp = event.timestamp ? new Date(event.timestamp).toLocaleString() : '—';

  return (
    <div style={{
      borderTop: `1px solid ${S[200]}`, padding: '16px 20px',
      background: S[50], borderRadius: '0 0 8px 8px',
    }}>
      {/* ── Metadata Grid ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: '0 32px', marginBottom: stages.length > 0 ? 16 : 0,
      }}>
        <div>
          <DetailRow icon={FileText} label="Execution ID" value={event.executionId} mono />
          <DetailRow icon={Shield} label="Type" value={isMcp ? `MCP Tool: ${event.toolName}` : 'GEL Governed Execution'} />
          <DetailRow icon={Activity} label="Decision" value={event.decision || event.policyDecision || 'ALLOWED'} />
          <DetailRow icon={Clock} label="Duration" value={
            event.totalDurationMs != null ? `${event.totalDurationMs}ms`
            : event.durationMs != null ? `${event.durationMs}ms` : '—'
          } />
        </div>
        <div>
          <DetailRow icon={Hash} label="Agent ID" value={event.agentId} mono />
          <DetailRow icon={Eye} label="Rule ID" value={event.ruleId && event.ruleId !== 'none' ? event.ruleId : 'No rule matched'} mono />
          <DetailRow icon={AlertTriangle} label="Violations" value={event.violations || (event.decision === 'BLOCKED' ? 'Policy enforcement blocked' : 'None')} />
          <DetailRow icon={Clock} label="Timestamp" value={timestamp} />
          {event.ruleVersion && <DetailRow label="Rule Version" value={`v${event.ruleVersion}`} />}
          {event.stageCount > 0 && <DetailRow label="Stages Evaluated" value={event.stageCount} />}
          {event.warnings > 0 && <DetailRow label="Warnings" value={event.warnings} />}
        </div>
      </div>

      {/* ── 6-Layer Policy Enforcement Pipeline ── */}
      {stages.length > 0 && (
        <div>
          <div style={{
            fontSize: 12, fontWeight: 700, color: S[700], marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Shield size={14} color={BRAND.primary} />
            6-Layer Policy Enforcement Pipeline
          </div>
          <div style={{ fontSize: 11, color: S[400], marginBottom: 10 }}>
            Every invocation passes through all enforcement stages. A BLOCK at any stage short-circuits execution.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {stages.map((stage, i) => <StageRow key={i} stage={stage} index={i} totalStages={stages.length} />)}
          </div>
        </div>
      )}

      {/* ── No stages fallback ── */}
      {stages.length === 0 && (
        <div style={{
          padding: '10px 14px', background: '#fff', borderRadius: 6,
          border: `1px solid ${S[200]}`, fontSize: 12, color: S[500],
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Eye size={14} color={S[400]} />
          {isMcp
            ? 'No enforcement stages recorded for this invocation. Trigger a tool call with policy rules active to see the full pipeline.'
            : 'Detailed stage results available for GEL governed executions.'}
        </div>
      )}
    </div>
  );
};

const ExecCard = ({ event, isNew, onExpand, expanded }) => {
  const isBlocked = event.decision === 'BLOCKED' || event.phase === 'BLOCKED';
  const isExecuting = event.phase === 'EXECUTING' || event.decision === 'EXECUTING';
  const isMcp = event.type === 'mcp';
  const hasWarnings = (event.warnings && event.warnings > 0) || (event.stageCount && event.stageCount > 0) || (event.policyStages && event.policyStages.length > 0);

  const borderLeft = isBlocked ? '3px solid #f43f5e'
    : isExecuting ? '3px solid #f59e0b'
    : hasWarnings && event.decision === 'ALLOWED' ? '3px solid #f59e0b'
    : isNew ? `3px solid ${BRAND.primary}` : `1px solid ${S[200]}`;

  const time = event.timestamp ? new Date(event.timestamp).toLocaleTimeString('en-US', { hour12: false }) : '—';

  return (
    <div className={`lec-card ${isNew ? 'lec-card-new' : ''}`}
      style={{ background: '#fff', border: `1px solid ${S[200]}`, borderLeft, borderRadius: 8, transition: 'all 0.15s' }}>

      {/* ── Summary Row (clickable) ── */}
      <div onClick={() => onExpand && onExpand(event)}
        style={{
          padding: '12px 16px', display: 'grid', cursor: 'pointer',
          gridTemplateColumns: '40px 1fr 140px 90px 80px 80px 28px',
          alignItems: 'center', gap: 10,
        }}>
        <PhaseIcon event={event} />

        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: S[900],
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {isMcp ? `mcp:${event.toolName || 'unknown'}` : `governed_execute · "${event.skillName || event.agentName || '—'}"`}
          </div>
          <div style={{ fontSize: 11, color: S[400], fontFamily: "'Consolas', 'Monaco', monospace" }}>
            {event.executionId ? event.executionId.substring(0, 16) : '—'}
            {isMcp ? ' · MCP Tool Invocation' : ' · GEL Envelope'}
            {event.ruleId && event.ruleId !== 'none' ? ` · Rule: ${event.ruleId.substring(0, 8)}` : ''}
            {event.hash ? ` · Hash: ${event.hash.substring(0, 4)}…${event.hash.slice(-4)}` : ''}
          </div>
        </div>

        <MiniPipeline event={event} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <DecisionBadge decision={event.decision || 'ALLOWED'} hasWarnings={hasWarnings} />
          {hasWarnings && event.warnings > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 10,
              background: ACCENT.amber.bg, color: ACCENT.amber.text, border: `1px solid ${ACCENT.amber.border}`,
              letterSpacing: '0.02em',
            }}>
              ⚠ {event.warnings} {event.warnings === 1 ? 'Warning' : 'Warnings'}
            </span>
          )}
        </div>

        <div style={{ fontSize: 12, color: S[600], fontFamily: "'Consolas', monospace" }}>
          {event.totalDurationMs != null ? `${event.totalDurationMs}ms` : event.durationMs != null ? `${event.durationMs}ms` : '—'}
        </div>
        <div style={{ fontSize: 11, color: S[400] }}>{time}</div>
        <div style={{ color: S[400], fontSize: 16, textAlign: 'center', transition: 'transform 0.2s' }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>
      </div>

      {/* ── Expanded Detail Panel ── */}
      {expanded && <EventDetailPanel event={event} />}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   CONNECTION STATUS PILL
   ═══════════════════════════════════════════════════════════════════════════════ */

const ConnectionStatus = ({ status }) => {
  const configs = {
    connected:    { bg: ACCENT.emerald.bg, color: ACCENT.emerald.text, border: ACCENT.emerald.border, label: 'Stream Connected', dot: '#10b981' },
    connecting:   { bg: ACCENT.amber.bg, color: ACCENT.amber.text, border: ACCENT.amber.border, label: 'Connecting...', dot: '#f59e0b' },
    disconnected: { bg: ACCENT.rose.bg, color: ACCENT.rose.text, border: ACCENT.rose.border, label: 'Disconnected', dot: '#f43f5e' },
  };
  const c = configs[status] || configs.disconnected;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      <span style={{
        width: 7, height: 7, background: c.dot, borderRadius: '50%',
        ...(status === 'connected' ? { animation: 'lecPulse 1.5s infinite' } : {}),
      }} />
      {c.label}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT: LiveExecutionCenter
   ═══════════════════════════════════════════════════════════════════════════════ */

const API_URL = process.env.REACT_APP_API_URL != null ? process.env.REACT_APP_API_URL : 'https://localhost:8443';

export default function LiveExecutionCenter({ profiles = [], loading: profilesLoading, agentScope }) {
  const [selectedAgentId, setSelectedAgentId] = useState('all');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    totalInvocations: 0, allowed: 0, blocked: 0, executing: 0, avgLatencyMs: 0,
  });
  const [autoScroll, setAutoScroll] = useState(true);
  const [eventCount, setEventCount] = useState(0);
  const [expandedEventId, setExpandedEventId] = useState(null);

  const eventSourceRef = useRef(null);
  const feedRef = useRef(null);
  const newEventIdsRef = useRef(new Set());

  // ── Get trust score for selected agent ──
  const selectedProfile = profiles.find(p => p.agentId === selectedAgentId);
  const trustScore = selectedProfile?.currentScore ?? '—';

  // ── SSE Connection ──
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setConnectionStatus('connecting');

    // Build SSE query params — include the agent's owning tenantId so the backend
    // registers the session under the correct tenant bucket (events arrive tagged
    // with the API key's tenant, which is the agent's tenant, not the viewer's).
    const params = new URLSearchParams();
    if (selectedAgentId !== 'all') {
      params.set('agentId', selectedAgentId);
      const agentProfile = profiles.find(p => p.agentId === selectedAgentId);
      if (agentProfile?.tenantId) {
        params.set('agentTenantId', agentProfile.tenantId);
      }
    }
    const queryString = params.toString();
    const url = `${API_URL}/api/v1/trust/live-stream${queryString ? '?' + queryString : ''}`;

    // Auth rides on the HttpOnly session cookie (SEC-25) via withCredentials —
    // EventSource can't carry a bearer header, which is why this used to be a
    // hand-rolled fetch + ReadableStream parser with a raw getAccessToken()
    // bearer (null after a page reload under cookie-auth, so the stream 401'd).
    // Routing through the shared createReconnectingEventSource gives the same
    // capped-backoff reconnect + single-flight token refresh as every other SSE
    // consumer, instead of a fixed 5s reconnect loop and a dead bearer.
    const NAMED_EVENTS = [
      'connected', 'execution_start', 'phase_update', 'execution_complete',
      'mcp_invocation', 'policy_gate', 'trust_update', 'stats',
    ];
    const eventHandlers = {};
    NAMED_EVENTS.forEach((name) => {
      // The helper hands us the raw MessageEvent; handleSSEEvent JSON-parses
      // ev.data exactly as the old line-parser did with currentEvent.data.
      eventHandlers[name] = (ev) => handleSSEEvent(name, ev.data);
    });

    eventSourceRef.current = createReconnectingEventSource({
      url,
      withCredentials: true,
      recover: refreshAccessToken,
      onOpen: () => setConnectionStatus('connected'),
      onError: () => setConnectionStatus('connecting'),
      events: eventHandlers,
    });

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [selectedAgentId, profiles]);

  // ── Handle incoming SSE events ──
  const handleSSEEvent = useCallback((eventType, rawData) => {
    try {
      const data = JSON.parse(rawData);

      switch (eventType) {
        case 'connected':
          console.log('[LiveExec] SSE connected:', data.streamId);
          break;

        case 'execution_start': {
          const newEvent = {
            id: data.executionId || `evt-${Date.now()}`,
            type: 'gel',
            executionId: data.executionId,
            agentId: data.agentId,
            agentName: data.agentName,
            skillName: data.skillName,
            agentType: data.agentType,
            phase: 'PRE_CHECK',
            decision: 'EXECUTING',
            timestamp: data.timestamp,
          };
          addEvent(newEvent);
          break;
        }

        case 'phase_update': {
          setEvents(prev => prev.map(e =>
            e.executionId === data.executionId
              ? { ...e, phase: data.phase, phaseDurationMs: data.phaseDurationMs }
              : e
          ));
          break;
        }

        case 'execution_complete': {
          setEvents(prev => prev.map(e =>
            e.executionId === data.executionId
              ? {
                  ...e,
                  phase: data.phase,
                  decision: data.decision,
                  totalDurationMs: data.totalDurationMs,
                  trustScoreAtExecution: data.trustScoreAtExecution,
                  stopReason: data.stopReason,
                }
              : e
          ));
          break;
        }

        case 'mcp_invocation': {
          const mcpEvent = {
            id: data.executionId || `mcp-${Date.now()}`,
            type: 'mcp',
            executionId: data.executionId,
            agentId: data.agentId,
            toolName: data.toolName,
            phase: 'COMPLETED',
            decision: data.decision || (data.success ? 'ALLOWED' : 'FAILED'),
            ruleId: data.ruleId,
            ruleVersion: data.ruleVersion,
            durationMs: data.durationMs,
            timestamp: data.timestamp,
            warnings: data.warnings || 0,
            stageCount: data.stageCount || 0,
            violations: data.violations,
            // Inline stage results — avoids ID mismatch with separate policy_gate events
            policyStages: data.stages || [],
          };
          addEvent(mcpEvent);
          break;
        }

        case 'policy_gate': {
          setEvents(prev => prev.map(e =>
            e.executionId === data.executionId
              ? { ...e, policyStages: data.stages, policyDecision: data.overallDecision }
              : e
          ));
          break;
        }

        case 'trust_update': {
          // Could update trust score display or add a trust event
          break;
        }

        case 'stats': {
          setStats(data);
          break;
        }

        default:
          break;
      }
    } catch (err) {
      console.warn('[LiveExec] Failed to parse SSE event:', err);
    }
  }, []);

  const addEvent = useCallback((event) => {
    newEventIdsRef.current.add(event.id);
    setEvents(prev => {
      const updated = [event, ...prev.slice(0, 99)]; // Keep last 100
      return updated;
    });
    setEventCount(prev => prev + 1);

    // Remove "new" animation after 3s
    setTimeout(() => {
      newEventIdsRef.current.delete(event.id);
    }, 3000);
  }, []);

  // ── Connect/Disconnect on agent change ──
  useEffect(() => {
    connectSSE();
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [selectedAgentId, connectSSE]);

  // ── Auto-scroll ──
  useEffect(() => {
    if (autoScroll && feedRef.current) {
      feedRef.current.scrollTop = 0; // Feed is reverse-chronological
    }
  }, [events, autoScroll]);

  // ── Expand handler (navigate to execution detail) ──
  const handleExpand = (event) => {
    setExpandedEventId(prev => prev === event.id ? null : event.id);
  };

  return (
    <div>
      <LiveStyles />

      {/* ── Agent Selector & Connection Status ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
        background: '#fff', padding: '14px 20px', borderRadius: 10,
        border: `1px solid ${S[200]}`, flexWrap: 'wrap',
      }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: S[600] }}>Agent:</label>
        <select
          value={selectedAgentId}
          onChange={(e) => {
            setSelectedAgentId(e.target.value);
            setEvents([]);
            setEventCount(0);
          }}
          style={{
            padding: '7px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
            fontSize: 13, background: '#fff', minWidth: 300, color: S[900],
            outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="all">All Agents (tenant-wide)</option>
          {profiles.map(p => (
            <option key={p.agentId} value={p.agentId}>
              {p.agentName || 'Agent'} — {p.agentId?.substring(0, 8)}…
            </option>
          ))}
        </select>

        <ConnectionStatus status={connectionStatus} />

        <div style={{ marginLeft: 'auto', fontSize: 11, color: S[400] }}>
          SSE: /api/v1/trust/live-stream
          {selectedAgentId !== 'all' ? `?agentId=${selectedAgentId.substring(0, 8)}…` : ''}
          {' · '}{eventCount} events
        </div>
      </div>

      {/* ── Live Stats Bar ── */}
      <div style={{
        display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap',
      }}>
        <StatChip icon={Activity} value={stats.totalInvocations || eventCount}
          label="Total Invocations" color={S[800]} accentBg={S[50]} />
        <StatChip icon={CheckCircle} value={stats.allowed || 0}
          label="Allowed" color="#10b981" accentBg={ACCENT.emerald.bg} />
        <StatChip icon={XCircle} value={stats.blocked || 0}
          label="Blocked" color="#f43f5e" accentBg={ACCENT.rose.bg} />
        <StatChip icon={Loader} value={Math.max(0, stats.executing || 0)}
          label="Executing" color="#f59e0b" accentBg={ACCENT.amber.bg} />
        <StatChip icon={Shield} value={typeof trustScore === 'number' ? trustScore.toFixed(1) : trustScore}
          label="Trust Score" color={typeof trustScore === 'number' && trustScore >= 75 ? '#10b981' : '#f59e0b'}
          accentBg={typeof trustScore === 'number' && trustScore >= 75 ? ACCENT.emerald.bg : ACCENT.amber.bg} />
        <StatChip icon={Clock} value={stats.avgLatencyMs ? `${stats.avgLatencyMs}ms` : '—'}
          label="Avg Latency" color={ACCENT.sky.icon} accentBg={ACCENT.sky.bg} />
      </div>

      {/* ── Feed Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Radio size={14} color={connectionStatus === 'connected' ? '#10b981' : S[400]}
            style={connectionStatus === 'connected' ? { animation: 'lecPulse 1.5s infinite' } : {}} />
          <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>Live Execution Feed</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: S[400] }}>
            {events.length} events · Auto-scroll {autoScroll ? 'ON' : 'OFF'}
          </span>
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            style={{
              padding: '4px 10px', fontSize: 11, fontWeight: 600,
              background: autoScroll ? ACCENT.emerald.bg : S[100],
              color: autoScroll ? ACCENT.emerald.text : S[600],
              border: `1px solid ${autoScroll ? ACCENT.emerald.border : S[200]}`,
              borderRadius: 6, cursor: 'pointer',
            }}
          >
            {autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
          </button>
        </div>
      </div>

      {/* ── Execution Feed ── */}
      <div ref={feedRef} style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        maxHeight: 'calc(100vh - 420px)', overflowY: 'auto',
        paddingRight: 4,
      }}>
        {events.length === 0 && (
          <div style={{
            padding: 48, textAlign: 'center', color: S[400],
            background: '#fff', borderRadius: 10, border: `1px solid ${S[200]}`,
          }}>
            <Radio size={24} color={S[300]} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
              {connectionStatus === 'connected'
                ? 'Waiting for live executions...'
                : connectionStatus === 'connecting'
                  ? 'Connecting to execution stream...'
                  : 'Not connected to execution stream'}
            </div>
            <div style={{ fontSize: 12, color: S[400] }}>
              {connectionStatus === 'connected'
                ? 'Events will appear here in real-time as agents execute through the GEL pipeline'
                : 'Select an agent and the stream will connect automatically'}
            </div>
          </div>
        )}

        {events.map((event) => (
          <ExecCard
            key={event.id}
            event={event}
            isNew={newEventIdsRef.current.has(event.id)}
            onExpand={handleExpand}
            expanded={expandedEventId === event.id}
          />
        ))}
      </div>
    </div>
  );
}
